import { format } from 'date-fns';
import { useMyUpcomingRenewalMembers } from '@/hooks/members/useMyUpcomingRenewalMembers';
import type { MemberDataset } from '@/lib/member-views/memberDataset.types';
import { sortFnTags } from '@/lib/member-views/sortRows';
import type { UpcomingRenewalMemberRow } from '@/lib/members/members.types';
import { reportOnly as reportOnlyFor } from './reportOnly';

const reportOnly = reportOnlyFor<UpcomingRenewalMemberRow>;

/**
 * Upcoming Renewals — the practice's Active members whose membership renews within the next 62 days,
 * for reaching out before the renewal date.
 *
 * The report is the 22 columns below, in `csv.order`: legacy's report less its Title column, which
 * this view's API doesn't return. Renewal and Last AWP are the table's own fields; the table's
 * Patient and Phone combine two values each, so the report has its own name and phone columns.
 */
export const upcomingRenewalMembersDataset: MemberDataset<UpcomingRenewalMemberRow> = {
  key: 'renewals',
  rowKey: (row) => row.id,
  useRows: useMyUpcomingRenewalMembers,
  alphaKey: (row) => row.lastName,
  // Soonest renewal first is what the view is for; the server returns the farthest first.
  defaultSort: [{ id: 'renewal', desc: false }],
  // The same provisional ceiling as All Active; these members are a subset of it.
  loading: { mode: 'full', ceiling: 10_000 },
  // Local date, not `toISOString()`, which is already tomorrow on a US evening.
  csvFileName: ({ date }) => `My_Patient_Upcoming_Renewals_${format(date, 'yyyy-MM-dd')}.csv`,
  fields: [
    {
      id: 'patient',
      label: 'memberViews.columns.patient',
      width: 'xl',
      // Sorts on "last first"; the cell shows the name the other way round, with the MDVIP ID.
      value: (row) => [row.lastName, row.firstName].filter(Boolean).join(' ') || null,
      table: true,
      sortable: true,
    },
    {
      id: 'renewal',
      label: 'memberViews.columns.renewal',
      width: 'sm',
      value: (row) => row.renewalDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 5, label: 'memberViews.csv.columns.renewalDate' },
    },
    {
      id: 'lastAwp',
      label: 'memberViews.columns.lastAwp',
      width: 'sm',
      value: (row) => row.lastAwpDate,
      format: 'date',
      table: true,
      sortable: true,
      csv: { order: 13, label: 'memberViews.csv.columns.lastAwpDate' },
    },
    {
      id: 'phone',
      label: 'memberViews.columns.phone',
      width: 'lg',
      value: (row) => row.mobilePhone ?? row.phone,
      format: 'phone',
      table: true,
    },
    {
      id: 'priorityTags',
      label: 'memberViews.columns.priorityTags',
      width: 'xl',
      // No tags is a missing value, so untagged rows sort last both ways, as the design asks.
      value: (row) => (row.priorityTags.length > 0 ? row.priorityTags : null),
      table: true,
      sortable: true,
      sortFn: sortFnTags,
    },
    reportOnly('lastName', 1, 'memberViews.csv.columns.lastName', (row) => row.lastName),
    reportOnly('firstName', 2, 'memberViews.csv.columns.firstName', (row) => row.firstName),
    reportOnly('mdvipId', 3, 'memberViews.csv.columns.mdvipId', (row) => row.mdvipId),
    reportOnly('established', 4, 'memberViews.csv.columns.dateEstablished', (row) => row.memberSince, 'date'),
    reportOnly('mailingStreet', 6, 'memberViews.csv.columns.mailingStreet', (row) => row.mailingStreet),
    reportOnly('city', 7, 'memberViews.csv.columns.city', (row) => row.mailingCity),
    reportOnly('state', 8, 'memberViews.csv.columns.state', (row) => row.mailingState),
    reportOnly('zip', 9, 'memberViews.csv.columns.zip', (row) => row.mailingZip),
    // The phone numbers as the API returns them, as legacy's report printed them.
    reportOnly('homePhone', 10, 'memberViews.csv.columns.phone', (row) => row.phone),
    reportOnly('cellPhone', 11, 'memberViews.csv.columns.cellPhone', (row) => row.mobilePhone),
    reportOnly('email', 12, 'memberViews.csv.columns.email', (row) => row.email),
    reportOnly('status', 14, 'memberViews.csv.columns.status', (row) => row.memberStatus),
    reportOnly('dob', 15, 'memberViews.csv.columns.dob', (row) => row.dateOfBirth, 'date'),
    reportOnly(
      'paymentSchedule',
      16,
      'memberViews.csv.columns.paymentSchedule',
      (row) => row.currentAgreementPaymentSchedule,
    ),
    // The current agreement's fee, not the membership's list price.
    reportOnly(
      'membershipFee',
      17,
      'memberViews.csv.columns.membershipFee',
      (row) => row.currentAgreementMsrp,
      'currency',
    ),
    reportOnly('autoPay', 18, 'memberViews.csv.columns.autoPay', (row) => row.autoPay, 'yesBlank'),
    reportOnly('lastPaymentDate', 19, 'memberViews.csv.columns.lastPaymentDate', (row) => row.lastPaymentDate, 'date'),
    reportOnly(
      'lastPaymentAmount',
      20,
      'memberViews.csv.columns.lastPaymentAmount',
      (row) => row.lastPaymentAmount,
      'currency',
    ),
    // "No" survives here, unlike Auto Pay: legacy printed this field's own Yes/No.
    reportOnly(
      'billToCorporateAccount',
      21,
      'memberViews.csv.columns.billToCorporateAccount',
      (row) => row.billToNationalAccount,
      'yesNoBlank',
    ),
    reportOnly('corporateAccount', 22, 'memberViews.csv.columns.corporateAccount', (row) => row.nationalAccountName),
  ],
};
